import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StyledProps } from '../types/styled-utils';

const MusicSectionContainer = styled.section`
  padding: 6rem 2rem;
  position: relative;
  /* Remove solid background to allow particles to show through */
  /* background-color: ${(props: StyledProps) => props.theme.colors.darkBg}; */
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 4rem 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  
  span {
    background: linear-gradient(
      to right,
      ${(props: StyledProps) => props.theme.colors.gradientStart},
      ${(props: StyledProps) => props.theme.colors.gradientEnd}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
`;

const MusicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(18, 20, 37, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(3px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba(110, 86, 207, 0.2);
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const ProjectDescription = styled.p`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const Links = styled.div`
  display: flex;
  gap: 1rem;
`;

const LinkIcon = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SpotifyFrame = styled.iframe`
  border: none;
  width: 100%;
  height: 80px;
  border-radius: 8px;
`;

const ViewMoreButton = styled.a`
  display: block;
  text-align: center;
  margin: 3rem auto 0;
  padding: 0.8rem 2rem;
  background: transparent;
  color: ${(props: StyledProps) => props.theme.colors.primary};
  font-weight: 600;
  border-radius: 4px;
  border: 2px solid ${(props: StyledProps) => props.theme.colors.primary};
  transition: all 0.3s ease;
  max-width: 200px;
  
  &:hover {
    background: rgba(110, 86, 207, 0.1);
    transform: translateY(-3px);
  }
`;

const YoutubeHeader = styled.div`
  background: rgba(40, 40, 40, 0.05);
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const YoutubeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #FF0000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const YoutubeInfo = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const YoutubeTitleSmall = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const YoutubeTitleLarge = styled.span`
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

// New component for Ecliptic project
const EclipticHeader = styled.div`
  background: rgba(40, 40, 40, 0.05);
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const EclipticIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6E56CF, #4CC38A);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const EclipticInfo = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EclipticTitleSmall = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const EclipticTitleLarge = styled.span`
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const ProjectsSection: React.FC = () => {
  const items = [
    {
      title: "Featured Artist",
      description: "Check out all tracks on Spotify",
      embedUrl: "https://open.spotify.com/embed/artist/6jSs5ecozKPDZ3LNFwlHH7?utm_source=generator",
      spotifyLink: "https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7",
      youtubeLink: "https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA",
      type: "spotify"
    },
    {
      title: "Ecliptic",
      description: "AI-Powered Beat-Synchronized Video Editor",
      websiteLink: "https://ecliptic.gg",
      githubLink: "https://github.com/modeofO",
      type: "project"
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 * i,
        duration: 0.5,
      }
    })
  };

  // Add GitHub icon SVG
  const GitHubIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.236 1.838 1.236 1.07 1.835 2.807 1.305 3.492.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.605-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );

  return (
    <MusicSectionContainer id="music">
      <SectionHeading>
        Projects & <span>Music</span>
      </SectionHeading>
      <SectionSubheading>
        Check out my music and development projects.
      </SectionSubheading>
      
      <MusicGrid>
        {items.map((item, index) => (
          <ProjectCard
            key={index}
            custom={index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {item.type === "spotify" ? (
              <SpotifyFrame 
                src={item.embedUrl}
                frameBorder="0" 
                allowFullScreen 
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                loading="lazy"
              />
            ) : item.type === "project" ? (
              <EclipticHeader>
                <EclipticIcon>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                </EclipticIcon>
                <EclipticInfo>
                  <EclipticTitleSmall>Development Project</EclipticTitleSmall>
                  <EclipticTitleLarge>AI Video Editor</EclipticTitleLarge>
                </EclipticInfo>
              </EclipticHeader>
            ) : (
              <YoutubeHeader>
                <YoutubeIcon>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </YoutubeIcon>
                <YoutubeInfo>
                  <YoutubeTitleSmall>YouTube Channel</YoutubeTitleSmall>
                  <YoutubeTitleLarge>Visual music content and more</YoutubeTitleLarge>
                </YoutubeInfo>
              </YoutubeHeader>
            )}
            <CardContent>
              <ProjectTitle>{item.title}</ProjectTitle>
              <ProjectDescription>{item.description}</ProjectDescription>
              
              <CardFooter>
                <Links>
                  {item.type === "spotify" && (
                    <LinkIcon 
                      href={item.spotifyLink}
                      target="_blank"
                      aria-label="Spotify"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.371-.721.49-1.101.241-3.021-1.851-6.819-2.271-11.279-1.241-.418.101-.838-.16-.931-.579-.101-.418.16-.838.579-.931 4.901-1.121 9.13-.601 12.491 1.441.371.241.49.721.241 1.069zm1.481-3.291c-.301.46-.921.621-1.381.311-3.461-2.131-8.731-2.751-12.821-1.501-.491.15-1.021-.13-1.171-.621-.15-.491.13-1.021.621-1.171 4.671-1.421 10.471-.721 14.461 1.771.448.301.609.921.291 1.211zm.141-3.401c-4.151-2.471-11.021-2.701-15.001-1.491-.631.191-1.291-.16-1.481-.781-.191-.621.16-1.291.781-1.481 4.581-1.391 12.191-1.121 17.001 1.731.571.331.761 1.081.421 1.661-.331.571-1.081.761-1.721.361z" />
                      </svg>
                    </LinkIcon>
                  )}
                  
                  {item.youtubeLink && (
                    <LinkIcon 
                      href={item.youtubeLink}
                      target="_blank"
                      aria-label="YouTube"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </LinkIcon>
                  )}
                  
                  {item.websiteLink && (
                    <LinkIcon 
                      href={item.websiteLink}
                      target="_blank"
                      aria-label="Website"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                    </LinkIcon>
                  )}
                  
                  {/* Only display GitHub link for project cards */}
                  {item.type === "project" && item.githubLink && (
                    <LinkIcon 
                      href={item.githubLink}
                      target="_blank"
                      aria-label="GitHub"
                    >
                      <GitHubIcon />
                    </LinkIcon>
                  )}
                </Links>
              </CardFooter>
            </CardContent>
          </ProjectCard>
        ))}
      </MusicGrid>
    </MusicSectionContainer>
  );
};

export default ProjectsSection; 